import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment, { type Moment } from 'moment';
import { SelectionCalendarSheet } from '../../components/common/SelectionCalendarSheet';
import {
  findFirstSelectableCheckoutStartYmd,
  isCheckoutStartDateSelectable,
  type CheckoutStartDateRules,
} from '../../data/domains/subscription';
import { formatMomentForApiYmd } from '../../shared/utils/dateUtils';
import { useTranslation } from '../../shared/i18n';
import { CheckoutDayOffWeekdayPicker } from './CheckoutDayOffWeekdayPicker';
import { type PlanDayOffOption } from '../../data/domains/checkout';

type Props = {
  visible: boolean;
  onClose: () => void;
  selected_date: Moment;
  selected_day_offs: number[];
  temp_day_off_options: PlanDayOffOption[];
  forced_day_offs: number[];
  max_day_off: number;
  checkout_date_rules: CheckoutStartDateRules;
  is_date_blocked?: (date_str: string) => boolean;
  min_month?: Moment;
  max_month?: Moment;
  on_save: (date: Moment, day_off_array: number[]) => void;
};

export function CheckoutStartDateCalendarSheet({
  visible,
  onClose,
  selected_date,
  selected_day_offs,
  temp_day_off_options,
  forced_day_offs,
  max_day_off,
  checkout_date_rules,
  is_date_blocked,
  min_month,
  max_month,
  on_save,
}: Props) {
  const { t } = useTranslation();
  const [draft_day_offs, set_draft_day_offs] = useState<number[]>(selected_day_offs);

  useEffect(() => {
    if (visible) {
      set_draft_day_offs(selected_day_offs);
    }
  }, [visible, selected_day_offs]);

  const draft_rules = useMemo(
    (): CheckoutStartDateRules => ({
      ...checkout_date_rules,
      userDayOffs: draft_day_offs,
    }),
    [checkout_date_rules, draft_day_offs],
  );

  const is_date_selectable = useCallback(
    (date_str: string) => isCheckoutStartDateSelectable(date_str, draft_rules),
    [draft_rules],
  );

  const show_day_off_picker = temp_day_off_options.length > 0 || forced_day_offs.length > 0;

  const header_content = show_day_off_picker ? (
    <CheckoutDayOffWeekdayPicker
      temp_day_off_options={temp_day_off_options}
      forced_day_offs={forced_day_offs}
      selected_day_offs={draft_day_offs}
      max_day_off={max_day_off}
      on_change={set_draft_day_offs}
    />
  ) : null;

  const handle_save = useCallback(
    (date: Moment) => {
      on_save(date.clone().startOf('day'), draft_day_offs.slice().sort((a, b) => a - b));
    },
    [draft_day_offs, on_save],
  );

  const repair_draft_date = useCallback(
    (date: Moment): Moment => {
      const ymd = formatMomentForApiYmd(date);
      if (is_date_selectable(ymd)) {
        return date.clone().startOf('day');
      }
      const next_ymd = findFirstSelectableCheckoutStartYmd(ymd, draft_rules);
      if (next_ymd) {
        return moment(next_ymd, 'YYYY-MM-DD', true).startOf('day');
      }
      const fallback = findFirstSelectableCheckoutStartYmd(draft_rules.minStartYmd, draft_rules);
      if (fallback) {
        return moment(fallback, 'YYYY-MM-DD', true).startOf('day');
      }
      return date.clone().startOf('day');
    },
    [draft_rules, is_date_selectable],
  );

  return (
    <SelectionCalendarSheet
      visible={visible}
      onClose={onClose}
      title={t('order.subscriptionStartDate')}
      selected_date={selected_date}
      on_save={handle_save}
      is_date_selectable={is_date_selectable}
      is_date_blocked={is_date_blocked}
      min_month={min_month}
      max_month={max_month}
      header_content={header_content}
      repair_selected_date={repair_draft_date}
    />
  );
}

export default CheckoutStartDateCalendarSheet;
